import { ContentService } from "@Api/ContentService";
import { Media } from "@Utilities/Media";
import { SearchBase } from "@Base/Site/SearchBase";
import { TextResource } from "@Utilities/TextResource";

import enquire from "enquire.js";
import Headroom from "headroom.js";
import MetisMenu from "metismenujs";
import Collapse from "bootstrap/js/dist/collapse";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import { CookieManager } from "@Utilities/CookieManager";

export class HeaderBase
{
	private readonly contentService: ContentService;
	public DesktopMenu: HTMLElement;
	public OffCanvasMenuInstance: Offcanvas;
	public OffCanvasMenuEl: HTMLElement;
	public Menu: HTMLElement;
	public MobileMenu: HTMLElement;
	public MobileMenuInstance: MetisMenu;
	public Search: SearchBase;
	public StickyHeader: Headroom;
	public TopMenu: HTMLElement;

	constructor()
	{
		this.contentService = new ContentService();
	}

	public Initialize(): void
	{
		this.Search = new SearchBase();
		this.ConfigureMobile();
	};

	static AddToBannerSection = (banner: string): void =>
	{
		const bannerSection = document.querySelector("#skin-customer-message");
		const bannerSectionHome = document.querySelector("#skin-customer-message-home");
		const frag = document.createRange().createContextualFragment(banner);
		const bannerNode = frag.firstChild;

		if (bannerSection !== null && bannerNode !== null)
		{
			bannerSection.append(bannerNode);
		}

		if (bannerSectionHome !== null && bannerNode !== null)
		{
			bannerSectionHome.append(bannerNode);
		}
	};

	public ConfigureMobile = (): void =>
	{
		enquire.register(Media.MediaMobile, {
			deferSetup: true,
			match: (): void =>
			{
				this.MenuMobileSetup();
				this.StickyHeaderSetup();
			},
			unmatch: (): void =>
			{
				this.MenuMobileDestroy();
				this.StickyHeaderDestroy();
			}
		});
	};

	public MenuMobileDestroy(): void
	{
		this.DesktopMenu = document.querySelector("#desktopMenu") as HTMLElement;
		this.Menu = document.querySelector("#menu") as HTMLElement;
		this.TopMenu = document.querySelector("#topMenu") as HTMLElement;

		if (this.OffCanvasMenuEl !== null && typeof this.OffCanvasMenuEl !== "undefined")
		{
			if (this.OffCanvasMenuEl.classList.contains("show"))
			{
				this.OffCanvasMenuInstance.hide();
				this.OffCanvasMenuEl.removeEventListener("show.bs.offcanvas", this.MenuMobileOpen);
			}
		}

		if (this.Menu !== null)
		{
			if (this.Menu.classList.contains("metismenu"))
			{
				if (typeof this.MobileMenuInstance !== "undefined")
				{
					this.MobileMenuInstance.dispose();
				}

				this.Menu.classList.remove("metismenu");
			}
		}

		if (this.DesktopMenu !== null)
		{
			this.DesktopMenu.appendChild(this.TopMenu);
		}

		document.querySelectorAll(".nav-unwrap__clone").forEach(element =>
		{
			if (element.parentNode !== null)
			{
				element.parentNode.removeChild(element);
			}
		});
	};

	public MenuMobileOpen = (): void =>
	{
		// setting variables. more happens in each implementation
		this.MobileMenu = document.querySelector("#mobileMenu") as HTMLElement;
		this.TopMenu = document.querySelector("#topMenu") as HTMLElement;
	};

	public MenuMobileSetup = (): void =>
	{
		this.OffCanvasMenuEl = document.querySelector("#offCanvasMenu") as HTMLElement;
		this.OffCanvasMenuInstance = Offcanvas.getOrCreateInstance("#offCanvasMenu");

		if (this.OffCanvasMenuEl !== null)
		{
			this.OffCanvasMenuEl.addEventListener("show.bs.offcanvas", this.MenuMobileOpen, { once: true });
		}
	};

	public StickyHeaderDestroy = (): void =>
	{
		if (typeof this.StickyHeader !== "undefined")
		{
			this.StickyHeader.destroy();
		}
	};

	public StickyHeaderSetup = (): void =>
	{
		const header = document.querySelector("header") as HTMLElement;
		const container = document.querySelector(".header-top") as HTMLElement;

		if (header !== null || container !== null)
		{
			this.StickyHeader = new Headroom(container, {
				offset: header.offsetHeight,
				onPin: (): void =>
				{
					header.classList.add("pinned");
					header.classList.remove("unpinned");
				},
				onUnpin: (): void =>
				{
					header.classList.add("unpinned");
					header.classList.remove("pinned");
					Collapse.getOrCreateInstance("#searchBar").hide();
				}
			});
			this.StickyHeader.init();
		}
	};

	public UpdateAccountName = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			if (ContentService.Response.AccountName !== "" && ContentService.Response.AccountName !== null)
			{
				const accountName = ContentService.Response.AccountName;
				const myAccountText = document.querySelectorAll(".menu-account .sgn");
				const myAccountContainers = document.querySelectorAll(".my-account");

				myAccountText.forEach(element =>
				{
					let name = "";

					if (accountName.length > 0)
					{
						name = `${TextResource.GetTextByCulture("Word_Greeting")}, ${accountName}`;
					}
					else
					{
						name = TextResource.GetTextByCulture("Feature_Header_AccountButton");
					}

					element.textContent = name;
				});

				myAccountContainers.forEach(container =>
				{
					if (accountName.length > 0)
					{
						container.querySelectorAll(".my-account--on").forEach(element =>
						{
							element.classList.remove("d-none");
							element.classList.add("d-block");
						});

						container.querySelectorAll(".my-account--off").forEach(element =>
						{
							element.classList.remove("d-block");
							element.classList.add("d-none");
						});
					}
					else
					{
						container.querySelectorAll(".my-account--on").forEach(element =>
						{
							element.classList.remove("d-block");
							element.classList.add("d-none");
						});

						container.querySelectorAll(".my-account--off").forEach(element =>
						{
							element.classList.remove("d-none");
							element.classList.add("d-block");
						});
					}
				});
			}
		}
	};

	public UpdateCartIcon = (itemOrderQuantity?: number): void =>
	{
		const cartButton = document.querySelector("#mcrt") as HTMLLinkElement;

		if (cartButton === null || typeof ContentService.Response === "undefined" || itemOrderQuantity === undefined)
		{
			return;
		}

		const cartPreorder = cartButton.querySelector(".pre") as HTMLElement;
		const quickCart = document.querySelector("#qcrt") as HTMLElement;
		let cartCount = 0;

		if (typeof ContentService.Response !== "undefined")
		{
			if (itemOrderQuantity == null)
			{
				itemOrderQuantity = ContentService.Response.TotalItemQuantity;
			}

			this.UpdateOrderItemIcon(itemOrderQuantity);

			const itemPreorderQuantity = ContentService.Response.PreOrderItemQuantity;
			const itemPreorderDropDownItem = document.querySelector(".my-cart .dropdown-item.preo");

			// Buy Icon
			if (itemPreorderQuantity > 0)
			{
				const tooltip = TextResource.ConstructSentence("Feature_Client_ItemQty", "0") + itemPreorderQuantity +
					TextResource.ConstructSentence("Feature_Client_ItemQty", "1") + (itemPreorderQuantity > 1 ? "s" : "") +
					TextResource.ConstructSentence("Feature_Client_ItemQty", "2");

				cartPreorder.textContent = itemPreorderQuantity.toString();
				cartPreorder.setAttribute("title", tooltip);
				cartPreorder.classList.remove("d-none");

				if (itemPreorderDropDownItem !== null)
				{
					itemPreorderDropDownItem.classList.remove("d-none");
				}

				cartCount++;
			}

			if (cartCount > 0)
			{
				cartButton.setAttribute("data-bs-toggle", "dropdown");
				cartButton.classList.add("dropdown-toggle");

				if (quickCart !== null)
				{
					quickCart.classList.add("d-none");
				}
			}
		}
	};

	private ProcessInternationalBanners = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			if (CookieManager.GetCookie("noIntl") !== "true" && ContentService.Response.WebsiteSuggestionBanner !== undefined && ContentService.Response.WebsiteSuggestionBanner !== null && ContentService.Response.WebsiteSuggestionBanner !== "")
			{
				const websiteSuggestionBanner = `<div id="intl-site">${ContentService.Response.WebsiteSuggestionBanner}</div>`;

				HeaderBase.AddToBannerSection(websiteSuggestionBanner);
			}
			else if (ContentService.Response.InternationalBanner !== undefined && ContentService.Response.InternationalBanner !== null && ContentService.Response.InternationalBanner !== "")
			{
				const intlBanner = `<div id="intl-ship">${ContentService.Response.InternationalBanner}</div>`;
				HeaderBase.AddToBannerSection(intlBanner);
			}
		}
	};

	public TriggerContentCallbacks = (): void =>
	{
		this.UpdateCartIcon();
		this.ProcessInternationalBanners();
		this.UpdateAccountName();
	};

	private UpdateOrderItemIcon = (itemOrderQuantity: number): void =>
	{
		const cartButton = document.querySelector("#mcrt") as HTMLLinkElement;

		if (!cartButton === null)
		{
			const cartBuy = cartButton.querySelector(".buy");

			// Buy Icon
			if (itemOrderQuantity > 0 && cartBuy !== null)
			{
				const tooltip = TextResource.ConstructSentence("Feature_Client_ItemQty", "0") + itemOrderQuantity +
					TextResource.ConstructSentence("Feature_Client_ItemQty", "1") + (itemOrderQuantity > 1 ? "s" : "") +
					TextResource.ConstructSentence("Feature_Client_ItemQty", "2");

				cartBuy.textContent = itemOrderQuantity.toString();
				cartBuy.setAttribute("title", tooltip);
				cartBuy.classList.remove("d-none");

				const cartDropdownItem = document.querySelector(".my-cart .dropdown-item.crt");

				if (cartDropdownItem !== null)
				{
					cartDropdownItem.classList.remove("d-none");
				}
			}
		}
	};
}